import React, { useMemo } from "react";
import { Box } from "@mui/joy";
import { motion, useReducedMotion } from "framer-motion";
import { Postcard, PostcardVariant } from "./Postcard";

interface PostcardCollageProps {
  onComplete?: () => void;
}

interface PostcardData {
  destination: string;
  variant: PostcardVariant;
  x: string; // final CSS left position
  y: string; // final CSS top position
  rotation: number;
  mobileHidden?: boolean;
  tabletHidden?: boolean;
  scale?: number;
}

const POSTCARDS: PostcardData[] = [
  { destination: "Paris", variant: "paris", x: "2%", y: "5%", rotation: -12 },
  { destination: "Tokyo", variant: "tokyo", x: "72%", y: "3%", rotation: 8 },
  {
    destination: "Santorini",
    variant: "santorini",
    x: "-3%",
    y: "55%",
    rotation: 6,
  },
  {
    destination: "Machu Picchu",
    variant: "machu-picchu",
    x: "78%",
    y: "60%",
    rotation: -10,
  },
  {
    destination: "New York",
    variant: "new-york",
    x: "18%",
    y: "72%",
    rotation: 14,
    tabletHidden: true,
  },
  {
    destination: "Marrakech",
    variant: "marrakech",
    x: "60%",
    y: "75%",
    rotation: -7,
    tabletHidden: true,
  },
  {
    destination: "Bali",
    variant: "bali",
    x: "35%",
    y: "2%",
    rotation: -5,
    mobileHidden: true,
  },
  {
    destination: "Rio de Janeiro",
    variant: "rio",
    x: "50%",
    y: "38%",
    rotation: 11,
    mobileHidden: true,
  },
];

const STAGGER_DELAY = 0.17; // seconds between each card

// Generate random off-screen starting positions
function getRandomOrigin(): { x: number; y: number } {
  const edge = Math.floor(Math.random() * 4);
  switch (edge) {
    case 0: // top
      return { x: Math.random() * 600 - 300, y: -400 };
    case 1: // right
      return { x: 600, y: Math.random() * 400 - 200 };
    case 2: // bottom
      return { x: Math.random() * 600 - 300, y: 400 };
    case 3: // left
    default:
      return { x: -600, y: Math.random() * 400 - 200 };
  }
}

export const PostcardCollage: React.FC<PostcardCollageProps> = ({
  onComplete,
}) => {
  const prefersReducedMotion = useReducedMotion();

  // Memoize random origins so they don't change on re-render
  const origins = useMemo(
    () => POSTCARDS.map(() => getRandomOrigin()),
    []
  );

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {/* Gradient orbs for depth */}
      <GradientOrbs reducedMotion={!!prefersReducedMotion} />

      {POSTCARDS.map((card, index) => {
        const origin = origins[index];
        const isLast = index === POSTCARDS.length - 1;

        return (
          <motion.div
            key={card.variant}
            initial={
              prefersReducedMotion
                ? false
                : {
                    x: origin.x,
                    y: origin.y,
                    scale: 0.3,
                    rotate: 0,
                    opacity: 0,
                  }
            }
            animate={{
              x: 0,
              y: 0,
              scale: card.scale || 1,
              rotate: card.rotation,
              opacity: 1,
            }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : {
                    type: "spring",
                    stiffness: 80,
                    damping: 15,
                    delay: index * STAGGER_DELAY,
                  }
            }
            onAnimationComplete={
              isLast && !prefersReducedMotion ? onComplete : undefined
            }
            style={{
              position: "absolute",
              left: card.x,
              top: card.y,
              willChange: "transform",
            }}
            className={[
              "postcard-item",
              card.mobileHidden ? "postcard-hide-mobile" : "",
              card.tabletHidden ? "postcard-hide-tablet" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <Postcard
              destination={card.destination}
              variant={card.variant}
            />
          </motion.div>
        );
      })}

      {/* Responsive hiding styles */}
      <style>{`
        @media (max-width: 767px) {
          .postcard-hide-mobile,
          .postcard-hide-tablet {
            display: none !important;
          }
          .postcard-item {
            transform-origin: center center;
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .postcard-hide-tablet {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
};

// Background gradient orbs
const GradientOrbs: React.FC<{ reducedMotion: boolean }> = ({
  reducedMotion,
}) => {
  const orbStyle = (
    color: string,
    size: number,
    top: string,
    left: string,
    duration: number
  ): React.CSSProperties => ({
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
    top,
    left,
    opacity: 0.12,
    animation: reducedMotion ? "none" : `float-orb ${duration}s ease-in-out infinite alternate`,
    pointerEvents: "none",
  });

  return (
    <>
      <div style={orbStyle("rgba(200, 145, 42, 0.8)", 500, "-10%", "-5%", 10)} />
      <div style={orbStyle("rgba(91, 155, 181, 0.8)", 400, "50%", "60%", 12)} />
      <div style={orbStyle("rgba(34, 117, 106, 0.8)", 350, "70%", "10%", 8)} />
      <style>{`
        @keyframes float-orb {
          0% { transform: translate(0, 0); }
          100% { transform: translate(30px, -20px); }
        }
      `}</style>
    </>
  );
};
